// ---------------------------------------------------------------------------
// localStorage fallback store + safe API fetcher for CMS module
// ---------------------------------------------------------------------------

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminFetch } from "@/lib/admin-api";

// ---------------------------------------------------------------------------
// localStorage keys
// ---------------------------------------------------------------------------

export const LS_KEYS = {
  heroSlides: "cms_hero_slides",
  brands: "cms_brands",
  promotions: "cms_promotions",
  offlineFlags: "cms_offline_flags",
} as const;

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function readLocalStore<T>(key: string, fallback: readonly T[]): readonly T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as readonly T[];
  } catch {
    return fallback;
  }
}

function writeLocalStore<T>(key: string, data: readonly T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // localStorage full — silently degrade
  }
}

function setOfflineFlag(module: string, offline: boolean): void {
  try {
    const flags = JSON.parse(
      localStorage.getItem(LS_KEYS.offlineFlags) ?? "{}",
    ) as Record<string, boolean>;
    const next = { ...flags, [module]: offline };
    localStorage.setItem(LS_KEYS.offlineFlags, JSON.stringify(next));
  } catch {
    // silent
  }
}

export function getOfflineFlag(module: string): boolean {
  try {
    const flags = JSON.parse(
      localStorage.getItem(LS_KEYS.offlineFlags) ?? "{}",
    ) as Record<string, boolean>;
    return flags[module] === true;
  } catch {
    return false;
  }
}

export function generateId(): string {
  return `local_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

// ---------------------------------------------------------------------------
// Safe fetcher with localStorage fallback
// ---------------------------------------------------------------------------

async function safeCmsFetch<T>(
  path: string,
  lsKey: string,
  extractKey: string,
): Promise<{ data: readonly T[]; offline: boolean }> {
  try {
    const result = await adminFetch<Record<string, readonly T[]>>(path);
    const items = result[extractKey] ?? [];
    writeLocalStore(lsKey, items);
    setOfflineFlag(lsKey, false);
    return { data: items, offline: false };
  } catch {
    setOfflineFlag(lsKey, true);
    return { data: readLocalStore<T>(lsKey, []), offline: true };
  }
}

/**
 * Mutation call with localStorage-first optimistic update semantics.
 *
 * Throws on API failure so the caller's `useMutation({ onError })` fires.
 * Callers MUST revert their localStorage write when this throws, otherwise
 * the UI ends up holding state the server never accepted (audit S3-C2 root
 * cause: this previously swallowed errors → every admin saw their changes
 * work locally but the server never had them).
 */
async function safeCmsMutate<T>(
  path: string,
  method: string,
  body: unknown,
): Promise<T> {
  return adminFetch<T>(path, {
    method,
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Generic CRUD hook over localStorage with API sync
// ---------------------------------------------------------------------------

interface UseLocalCrudOpts<T> {
  readonly queryKey: string;
  readonly apiPath: string;
  readonly lsKey: string;
  readonly extractKey: string;
  readonly defaults?: readonly T[];
}

export function useLocalCrud<T extends { readonly id: string }>(
  opts: UseLocalCrudOpts<T>,
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [opts.queryKey],
    queryFn: async () => {
      const result = await safeCmsFetch<T>(
        opts.apiPath,
        opts.lsKey,
        opts.extractKey,
      );
      if (
        result.data.length === 0 &&
        opts.defaults &&
        opts.defaults.length > 0
      ) {
        writeLocalStore(opts.lsKey, opts.defaults);
        return { data: opts.defaults, offline: result.offline };
      }
      return result;
    },
    staleTime: 30_000,
  });

  const items = query.data?.data ?? [];
  const isOffline = query.data?.offline ?? getOfflineFlag(opts.lsKey);

  // API-first mutations with optimistic localStorage write + rollback on API
  // failure. Previously the localStorage write was committed even when the
  // API call failed silently — admins saw their changes "work" locally but
  // nothing reached the server (audit S3-C2 root cause).

  const createMutation = useMutation({
    mutationFn: async (newItem: T) => {
      const current = readLocalStore<T>(opts.lsKey, []);
      const updated = [...current, newItem];
      writeLocalStore(opts.lsKey, updated);
      try {
        await safeCmsMutate(opts.apiPath, "POST", newItem);
      } catch (err) {
        writeLocalStore(opts.lsKey, current);
        throw err;
      }
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [opts.queryKey] });
      toast.success("Cr\u00E9\u00E9 avec succ\u00E8s");
    },
    onError: (err) => {
      queryClient.invalidateQueries({ queryKey: [opts.queryKey] });
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : "Erreur lors de la cr\u00E9ation",
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (updatedItem: T) => {
      const current = readLocalStore<T>(opts.lsKey, []);
      const updated = current.map((item) =>
        item.id === updatedItem.id ? updatedItem : item,
      );
      writeLocalStore(opts.lsKey, updated);
      try {
        await safeCmsMutate(
          `${opts.apiPath}/${updatedItem.id}`,
          "PATCH",
          updatedItem,
        );
      } catch (err) {
        writeLocalStore(opts.lsKey, current);
        throw err;
      }
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [opts.queryKey] });
      toast.success("Mis \u00E0 jour avec succ\u00E8s");
    },
    onError: (err) => {
      queryClient.invalidateQueries({ queryKey: [opts.queryKey] });
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : "Erreur lors de la mise \u00E0 jour",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const current = readLocalStore<T>(opts.lsKey, []);
      const updated = current.filter((item) => item.id !== id);
      writeLocalStore(opts.lsKey, updated);
      try {
        await safeCmsMutate(`${opts.apiPath}/${id}`, "DELETE", null);
      } catch (err) {
        writeLocalStore(opts.lsKey, current);
        throw err;
      }
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [opts.queryKey] });
      toast.success("Supprim\u00E9 avec succ\u00E8s");
    },
    onError: (err) => {
      queryClient.invalidateQueries({ queryKey: [opts.queryKey] });
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : "Erreur lors de la suppression",
      );
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (reorderedItems: readonly T[]) => {
      const current = readLocalStore<T>(opts.lsKey, []);
      writeLocalStore(opts.lsKey, reorderedItems);
      try {
        await safeCmsMutate(`${opts.apiPath}/reorder`, "PUT", {
          items: reorderedItems.map((item, i) => ({
            id: item.id,
            sort_order: i + 1,
          })),
        });
      } catch (err) {
        writeLocalStore(opts.lsKey, current);
        throw err;
      }
      return reorderedItems;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [opts.queryKey] });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: [opts.queryKey] });
      toast.error("Erreur lors de la r\u00E9organisation");
    },
  });

  return {
    items,
    isOffline,
    isLoading: query.isLoading,
    createItem: createMutation.mutate,
    updateItem: updateMutation.mutate,
    deleteItem: deleteMutation.mutate,
    reorderItems: reorderMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
